import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, MapPin, AlertTriangle, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import VerificationLabel from "@/components/VerificationLabel";
import { api } from "@/lib/api";

/**
 * Shared detail page for /community-colleges/[slug] and /universities/[slug].
 * Placeholder records render a "Profile Coming Soon" state + noindex.
 */
export default function InstitutionDetail({ variant = "community_college" }) {
  const { slug } = useParams();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isCC = variant === "community_college";
  const basePath = isCC ? "/community-colleges" : "/universities";
  const endpoint = isCC ? "/community-colleges" : "/universities";

  useEffect(() => {
    setLoading(true);
    setError(false);
    api.get(`${endpoint}/${slug}`)
      .then((r) => setInst(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug, endpoint]);

  if (loading) return <Layout><div className="max-w-4xl mx-auto p-16 text-stone">Loading…</div></Layout>;
  if (error || !inst) return (
    <Layout>
      <div className="max-w-4xl mx-auto p-16">
        <h1 className="font-serif text-4xl text-ivy">Not found</h1>
        <p className="mt-4 text-ink-soft">Return to the <Link className="text-brass underline" to={basePath}>directory</Link>.</p>
      </div>
    </Layout>
  );

  const isPlaceholder = inst._placeholder || inst.verification_status !== "verified";
  const location = inst.location ? [inst.location.city, inst.location.state].filter(Boolean).join(", ") : null;
  const typeLabel = inst.type === "community_college" ? "Community College" : "State University";

  const jsonLd = !isPlaceholder ? {
    "@context": "https://schema.org",
    "@type": inst.type === "community_college" ? "CollegeOrUniversity" : "EducationalOrganization",
    "name": inst.name,
    "url": inst.website,
    "address": location ? { "@type": "PostalAddress", "addressLocality": inst.location.city, "addressRegion": inst.location.state, "addressCountry": "US" } : undefined,
  } : null;

  return (
    <Layout>
      <SEO
        title={`${inst.name} Transfer Guide | Beyond Valencia`}
        description={isPlaceholder
          ? `${inst.name} — Florida transfer profile coming soon. Ask NEO about ${inst.name} or schedule a Transfer Audit.`
          : (inst.body_markdown || "").slice(0, 200).replace(/\n/g, " ")}
        canonical={`${basePath}/${slug}`}
        noindex={isPlaceholder}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="bg-ivy text-bone border-b border-ivy-light" data-testid="institution-detail-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-[10px] uppercase tracking-[0.24em] text-brass-soft font-medium">{typeLabel}</span>
            <VerificationLabel status={inst.verification_status || "awaiting"} />
          </div>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[3.75rem] tracking-tight leading-[1.05]" data-testid="institution-detail-name">
            {inst.name}
          </h1>
          {location && (
            <p className="mt-5 flex items-center gap-2 text-sm text-bone-dim uppercase tracking-[0.18em]">
              <MapPin className="h-3.5 w-3.5 text-brass" /> {location}
            </p>
          )}
          {inst.website && (
            <a href={inst.website} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-brass hover:text-brass-soft transition-colors border-b border-brass/50">
              <ExternalLink className="h-3.5 w-3.5" /> {inst.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </section>

      {/* BODY */}
      {isPlaceholder ? (
        <PlaceholderProfile inst={inst} />
      ) : (
        <VerifiedProfile inst={inst} />
      )}
    </Layout>
  );
}

function PlaceholderProfile({ inst }) {
  return (
    <section className="bg-bone" data-testid="institution-placeholder">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <div className="border border-brass/40 bg-brass/5 p-8 lg:p-10">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-semibold">Profile Coming Soon</div>
          <h2 className="mt-4 font-serif font-medium text-3xl sm:text-4xl text-ivy tracking-tight leading-tight">
            This institution is being verified by our transfer research team.
          </h2>
          <p className="mt-6 text-base text-ink-soft leading-relaxed">
            We&rsquo;re building the definitive profile for {inst.name} — including major-specific transfer requirements,
            common pitfalls, and Beyond Valencia&rsquo;s strategic read. Until then, here&rsquo;s how to get answers today:
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/neo" className="inline-flex items-center gap-2 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors" data-testid="placeholder-cta-neo">
              Ask NEO about {inst.name.split(" ")[0]} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/transfer-audit" className="inline-flex items-center gap-2 border border-ivy hover:bg-ivy hover:text-bone text-ivy uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors" data-testid="placeholder-cta-audit">
              Book a Transfer Audit
            </Link>
          </div>
          {inst.official_source_url && (
            <p className="mt-6 text-xs text-stone">
              Official source: <a href={inst.official_source_url} target="_blank" rel="noopener noreferrer" className="text-brass border-b border-brass/50">{inst.official_source_url}</a>
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function VerifiedProfile({ inst }) {
  return (
    <section className="bg-bone" data-testid="institution-verified">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
        <MarkdownBody markdown={inst.body_markdown} />

        {/* Warnings */}
        {inst.warnings && inst.warnings.length > 0 && (
          <div className="mt-14 border-l-4 border-crimson bg-crimson/5 px-6 py-5">
            <div className="flex items-baseline gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-crimson flex-shrink-0" />
              <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-crimson">Watch outs</div>
            </div>
            <ul className="space-y-2 text-sm text-ink-soft leading-relaxed">
              {inst.warnings.map((w, i) => <li key={i}>• {w}</li>)}
            </ul>
          </div>
        )}

        {/* University-only structured sections */}
        {inst.application_deadlines_by_term && (
          <FactBlock title="Application deadlines">
            <ul className="space-y-1 text-sm text-ink-soft">
              {Object.entries(inst.application_deadlines_by_term).map(([term, val]) => (
                <li key={term}><span className="uppercase tracking-[0.16em] text-[11px] text-brass font-semibold mr-3">{term}</span>{val}</li>
              ))}
            </ul>
          </FactBlock>
        )}
        {inst.major_specific_requirements?.length > 0 && (
          <FactBlock title="Major-specific requirements">
            <ul className="space-y-2 text-sm text-ink-soft">
              {inst.major_specific_requirements.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
          </FactBlock>
        )}
        {inst.foreign_language_requirements && (
          <FactBlock title="Foreign language requirement">
            <p className="text-sm text-ink-soft leading-relaxed">{inst.foreign_language_requirements}</p>
          </FactBlock>
        )}

        {/* Common destinations */}
        {inst.common_destinations?.length > 0 && (
          <FactBlock title="Common transfer destinations">
            <p className="text-sm text-ink-soft">{inst.common_destinations.join(" · ")}</p>
          </FactBlock>
        )}

        {/* Official source + last verified */}
        {(inst.official_source_url || inst.last_verified_date) && (
          <div className="mt-14 pt-6 border-t border-stone/30 text-xs text-stone flex flex-wrap gap-4">
            {inst.official_source_url && (
              <span>Source: <a href={inst.official_source_url} target="_blank" rel="noopener noreferrer" className="text-brass border-b border-brass/40">{inst.official_source_url.replace(/^https?:\/\//, "").slice(0, 60)}</a></span>
            )}
            {inst.last_verified_date && (
              <span>Last verified: {inst.last_verified_date.slice(0, 10)}</span>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-14 border border-brass/40 bg-brass/5 p-8 lg:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-semibold">Ready to plan your transfer?</div>
            <p className="mt-2 font-serif text-2xl text-ivy tracking-tight">Turn this profile into a personalized pathway.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/neo" className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors">Ask NEO</Link>
            <Link to="/transfer-audit" className="border border-ivy hover:bg-ivy hover:text-bone text-ivy uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors">Book a Transfer Audit</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FactBlock({ title, children }) {
  return (
    <div className="mt-12">
      <div className="text-[10px] uppercase tracking-[0.24em] text-brass font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

/** Minimal markdown renderer — headings, paragraphs, bullets, bold. */
function MarkdownBody({ markdown }) {
  if (!markdown) return null;
  // Split on blank lines first, but ALSO break each block into logical sub-parts
  // when a heading is immediately followed by content.
  const rawBlocks = markdown.split(/\n{2,}/);
  const blocks = [];
  for (const rb of rawBlocks) {
    const lines = rb.split("\n");
    let buf = [];
    const flush = () => { if (buf.length) { blocks.push(buf.join("\n")); buf = []; } };
    for (const ln of lines) {
      if (/^#{1,3}\s/.test(ln)) {
        flush();
        blocks.push(ln);
      } else if (/^-\s/.test(ln) || /^\d+\.\s/.test(ln)) {
        // List item — if previous buf was a paragraph, flush it first
        if (buf.length && !(/^-\s/.test(buf[0]) || /^\d+\.\s/.test(buf[0]))) flush();
        buf.push(ln);
      } else {
        // Paragraph line — if previous buf was a list, flush it first
        if (buf.length && (/^-\s/.test(buf[0]) || /^\d+\.\s/.test(buf[0]))) flush();
        buf.push(ln);
      }
    }
    flush();
  }
  return (
    <div className="prose-like space-y-6 text-[16px] text-ink leading-relaxed">
      {blocks.map((b, i) => {
        const t = b.trim();
        if (t.startsWith("### ")) return <h3 key={i} className="font-serif text-2xl text-ivy tracking-tight mt-8">{t.slice(4)}</h3>;
        if (t.startsWith("## ")) return <h2 key={i} className="font-serif text-3xl text-ivy tracking-tight mt-10">{t.slice(3)}</h2>;
        if (t.startsWith("# ")) return <h1 key={i} className="font-serif text-4xl text-ivy tracking-tight mt-10">{t.slice(2)}</h1>;
        if (/^\d+\.\s/.test(t)) {
          const items = t.split("\n").filter((l) => /^\d+\.\s/.test(l));
          return <ol key={i} className="list-decimal pl-6 space-y-2">{items.map((it, k) => <li key={k}>{inlineFormat(it.replace(/^\d+\.\s/, ""))}</li>)}</ol>;
        }
        if (t.startsWith("- ")) {
          const items = t.split("\n").filter((l) => l.startsWith("- "));
          return <ul key={i} className="space-y-1.5">{items.map((it, k) => <li key={k} className="flex gap-3"><span className="text-brass mt-2.5 h-px w-3 bg-brass flex-shrink-0" /><span>{inlineFormat(it.slice(2))}</span></li>)}</ul>;
        }
        return <p key={i}>{inlineFormat(t)}</p>;
      })}
    </div>
  );
}
function inlineFormat(text) {
  const html = text
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}
